import React, { useEffect, useState } from 'react';
import { format as numberFormat } from 'd3-format';
import { format as dateFormat } from 'date-fns';
import PropTypes from 'prop-types';
import { Button, Table } from 'reactstrap';
import LoadingIcon from 'mdi-react/LoadingIcon';
import TableArrowDownIcon from 'mdi-react/TableArrowDownIcon';
import ArrowTopIcon from 'mdi-react/ArrowTopIcon';
import ArrowLeftRightIcon from 'mdi-react/ArrowLeftRightIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import { Text } from 'recharts';
import CsvDownload from 'react-csv-downloader';
import PopoverRight from './PopoverRight';
import { SingleColumn } from '../metadata/AthenaTableMetaData';

const AthenaTable = ({
                       tableData, tableMetaData, isLoading, datesForCsvFile, downloadableAsCsv, csvFilePrefix,
}) => {
  const defaultSortedColumn = 'score_sum';

  const [tableContentData, setTableContentData] = useState(tableData);
  const [sortedColumn, setSorted] = useState(defaultSortedColumn);
  const [descendingSort, setDescendingSort] = useState(false);

  const downloadCsvColumns = tableMetaData.map(item => ({ id: item.varName, displayName: item.header }));
  const csvFileName = () => {
    const startDateString = dateFormat(datesForCsvFile.startDate, 'yyyy-MM-dd');
    const endDateString = dateFormat(datesForCsvFile.endDate, 'yyyy-MM-dd');
    return startDateString === endDateString
      ? csvFilePrefix + startDateString
      : `${csvFilePrefix}${startDateString}_${endDateString}`;
  };

  const sortTable = (varName, table, isDescending) => {
    console.log('sorting');
    if (isDescending) {
      table.sort((a, b) => {
        if (a[varName] < b[varName]) {
          return 1;
        }
        if (a[varName] > b[varName]) {
          return -1;
        }
        return 0;
      });
    } else {
      table.sort((a, b) => {
        if (a[varName] < b[varName]) {
          return -1;
        }
        if (a[varName] > b[varName]) {
          return 1;
        }
        return 0;
      });
    }
    return table;
  };

  const handleManualSort = (varName) => {
    setSorted(varName);
    setTableContentData(sortTable(varName, tableData, descendingSort));
    setDescendingSort(!descendingSort);
  };

  const handleDefaultSort = (varName) => {
    setSorted(varName);
    setTableContentData(sortTable(varName, tableData, true));
    setDescendingSort(false);
  };

  useEffect(() => {
    console.log('table contents changed');
    handleDefaultSort(defaultSortedColumn);
  }, [tableData]);

  const rotateStyle = {
    transform: descendingSort ? 'rotate(180deg)' : '',
    transition: 'transform 150ms ease', // smooth transition
  };

  const placeHeaderAndArrowIcon = (column) => {
    if (column.varName === sortedColumn) {
      return (
        <div style={{ whiteSpace: 'nowrap' }}>
          <Text
            role="button"
            onClick={() => handleManualSort(column.varName)}
          >
            {column.header}
          </Text>
          <ArrowTopIcon
            size="1.5em"
            color="DarkGray"
            style={rotateStyle}
            role="button"
            onClick={() => handleManualSort(column.varName)}
          />
        </div>
      );
    }
    if (column.sortable) {
      return (
        <div style={{ whiteSpace: 'nowrap' }}>
          <Text
            role="button"
            onClick={() => handleManualSort(column.varName)}
          >
            {column.header}
          </Text>
          <ArrowLeftRightIcon
            size="1.5em"
            color="DarkGray"
            role="button"
            onClick={() => handleManualSort(column.varName)}
          />
        </div>
      );
    }
    return (
      <div style={{ whiteSpace: 'nowrap' }}>
        <Text>
          {column.header}
        </Text>
      </div>
    );
  };

  const formatNumber = number => numberFormat('.3~s')(number).replace(/G/, 'B');

  const formatCurrency = number => numberFormat('$.3~s')(number).replace(/G/, 'B');

  const alternateTextColorInList = (dataList) => {
    const result = [];
    for (let i = 0; i < dataList.length; i += 1) {
      result.push(<span className={i % 2 === 1 ? 'text-primary' : 'text-info'}>{dataList[i]}</span>);
      result.push(<>, </>);
    }
    result.pop();
    return result;
  };

  const handleBigListPopover = (dataList, numberOfElementsBeforePopover, id) => {
    const result = [];
    result.push(alternateTextColorInList(dataList.slice(0, numberOfElementsBeforePopover)));

    if (dataList.length > numberOfElementsBeforePopover) {
      result.push(<PopoverRight
        dir="ltr"
        contents={alternateTextColorInList(dataList.slice(numberOfElementsBeforePopover))}
        id={id}
      />);
    }
    return result;
  };

  const generatePlatformHandleLink = (handleType, handle) => {
    if (handleType === 'facebook_handle') {
      return `https://www.facebook.com/${handle}`;
    }
    if (handleType === 'twitter_handle') {
      return `https://www.twitter.com/${handle}`;
    }
    if (handleType === 'linkedin_handle') {
      return `https://www.linkedin.com/${handle}`;
    }
    if (handleType === 'crunchbase_handle') {
      return `https://www.crunchbase.com/${handle}`;
    }
    return '';
  };

  const handleCellContent = (column, item) => {
    if (column.varType === 'BOOLEAN') {
      return item[column.varName].toString();
    }
    if (column.varType === 'NUMBER') {
      return formatNumber(item[column.varName]);
    }
    if (column.varType === 'CURRENCY') {
      return formatCurrency(item[column.varName]);
    }
    if (column.varType === 'LIST') {
      return handleBigListPopover(
        item[column.varName],
        column.defaultElementCount,
        `${item.domain.replaceAll('.', '_')}_${column.varName}`,
      );
    }
    if (column.varType === 'HYPERLINK') {
      if (item[column.varName] === '') {
        return (
          <span />
        );
      }
      return (
        <a
          className="text-danger"
          href={generatePlatformHandleLink(column.varName, item[column.varName])}
          target="_blank"
          rel="noreferrer"
        >
          {item[column.varName]}
          <OpenInNewIcon size="1.2em" style={{ marginBottom: '0.2em' }} />
        </a>
      );
    }
    return item[column.varName];
  };

  if (isLoading) {
    return (
      <div className="text-center">
        <LoadingIcon className="load__icon text-info" />
      </div>
    );
  }

  return (
    tableData.length === 0
      ? <span>No Data</span>
      : (
        <div>
          {
            tableContentData.length > 0 && !isLoading && downloadableAsCsv
              ? (
                <CsvDownload
                  filename={csvFileName()}
                  columns={downloadCsvColumns}
                  datas={tableContentData}
                  separator=","
                  wrapColumnChar={'"'}
                  className="btn btn-success text-white"
                >
                  <TableArrowDownIcon
                    style={{ marginBottom: '0.3em', width: '1.5em', height: '1.5em' }}
                  />Download as .csv
                </CsvDownload>
              )
              : null
          }
          <Table responsive striped bordered className="text-center">
            <thead>
              <tr>
                {tableMetaData.map(column => (
                  <th colSpan="1" role="columnheader" style={{ minWidth: column.minWidth }}>
                    {placeHeaderAndArrowIcon(column)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableContentData.map(item => (
                <tr>
                  {tableMetaData.map(column => (
                    <td>{handleCellContent(column, item)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        )
  );
};

AthenaTable.defaultProps = {
  datesForCsvFile: { startDate: new Date(), endDate: new Date() },
  downloadableAsCsv: false,
  csvFilePrefix: '',
};


AthenaTable.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  tableData: PropTypes.array.isRequired,
  tableMetaData: PropTypes.arrayOf(SingleColumn).isRequired,
  isLoading: PropTypes.bool.isRequired,
  downloadableAsCsv: PropTypes.bool,
  csvFilePrefix: PropTypes.string,
  datesForCsvFile: PropTypes.exact({
      startDate: PropTypes.instanceOf(Date).isRequired,
      endDate: PropTypes.instanceOf(Date).isRequired,
  }),
};

// datesForCsvFile: (props, propName, componentName) => {
//   if (props.downloadableAsCsv) {
//     if (props[propName]) {
//       const value = props[propName];
//       if (
//         value.endDate && value.endDate instanceof Date
//         && value.startDate && value.startDate instanceof Date
//       ) {
//         return null;
//       }
//     }
//     return new Error(`${componentName} needs a '${propName}' prop with
//     'startDate' and 'endDate' keys that hold 'Date' values.`);
//   }
//   return null;
// },
//   csvFilePrefix: (props, propName, componentName) => {
//   if (props.downloadableAsCsv) {
//     if (props[propName] && typeof props[propName] === 'string') {
//       return null;
//     }
//     return new Error(`ERROR IN ${componentName} with ${propName}.`);
//   }
//   return null;
// },

export default AthenaTable;

